import React, { useEffect, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import pluralize from 'utils/pluralize';
import { ACTIVATION, CPM_FORMAT } from 'utils/rest';

import { ButtonCreate } from 'components/ButtonCreate';
import { Text, Tooltip } from 'components/DataDisplay';
import { Select } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import Loading from 'components/Loading';
import { TableData } from 'components/TableData';

import { ModalDelete, ModalDuplicate, ModalForm } from './components';

const CpmFormatContainer = () => {
  const DOMAIN_GROUPID = 'domain';

  const { isSuperAdmin } = useUser();
  const { themeColors } = useTheme();
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);

  const [shopGroups, setShopGroups] = useState([]);
  const [shopGroupId, setShopGroupId] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [modalType, setModalType] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [medias, setMedias] = useState([]);
  const [allShopGroups, setAllShopGroups] = useState([]);
  const [mediaFormats, setMediaFormats] = useState({});

  const tooltipContent = list => {
    return (
      <Container>
        {list.map(item => (
          <Row spacing={0}>
            <Item>
              <Text color={themeColors.dark}>{item}</Text>
            </Item>
          </Row>
        ))}
      </Container>
    );
  };

  const formatList = (list, type) => {
    const count = list.length;
    if (count === 0) {
      return <Text>Aucun</Text>;
    }
    if (count === 1) return <Text color="pml"> {list[0]} </Text>;

    const others = count - 1;
    const str = ` ${others} ${pluralize('autre', others)} ${pluralize(
      type,
      others
    )}`;

    return (
      <Container>
        <Row spacing={2}>
          <Item flex>
            <Text color="pml"> {list[0]}&nbsp;</Text>
          </Item>
          <Item flex>
            <Text> et&nbsp;</Text>
          </Item>
          <Item flex>
            <Tooltip title={tooltipContent(list)}>
              <Text color="pml">{str}</Text>
            </Tooltip>
          </Item>
        </Row>
      </Container>
    );
  };

  const getDatas = async () => {
    const [
      formatResults,
      activationShopGroups,
      parentMedias
    ] = await Promise.all([
      CPM_FORMAT.getAll(),
      ACTIVATION.getActivationShopGoups(),
      ACTIVATION.getActivableMedias(false)
    ]);

    const domainFakeShopGroup = {
      id: DOMAIN_GROUPID,
      name: 'Domain (par défaut)'
    };

    const withShops = activationShopGroups.filter(el => !!el.shops?.length);

    const dvMedias = parentMedias.reduce((acc, el) => {
      if (el.model === 'dv360') {
        acc.push({ label: el.name, value: el.key });
      }
      return acc;
    }, []);

    const mediaAcceptedCreatives = await ACTIVATION.getMediaAcceptedCreativesByMedias(
      dvMedias.map(el => el.value)
    );

    const shopGroupsWithConfig = withShops.filter(el =>
      formatResults.find(config => config.shopGroupId === el.id)
    );

    const domainConfig = formatResults.find(config => !config.shopGroupId)
      ? [domainFakeShopGroup]
      : [];

    const mappedFormats = dvMedias.reduce((acc, { value: key }) => {
      const formats = mediaAcceptedCreatives.filter(
        el =>
          el.creativeType === 'image' &&
          el.activableMedia === key &&
          (el.name || (el.width && el.height))
      );

      const uniqueFormats = new Map();

      formats.forEach(({ name, width, height }) => {
        const value = `${width}x${height}`;

        if (!uniqueFormats.has(value)) {
          uniqueFormats.set(value, {
            label: name || value,
            key: value
          });
        }
      });

      acc[key] = Array.from(uniqueFormats.values());
      return acc;
    }, {});

    const mapped = formatResults.map(config => {
      const { formats, activableMedia } = config;

      const { name: media } = dvMedias.find(
        el => el.key === activableMedia
      ) || { name: activableMedia };

      const formatedFormats = formats.map(
        format =>
          mappedFormats[activableMedia]?.find(el => el.key === format)?.label ||
          format
      );

      return {
        ...config,
        media,
        listFormats: formatList(formatedFormats, 'autre'),
        shopGroupId: config.shopGroupId || DOMAIN_GROUPID
      };
    });

    const withConfig = [...domainConfig, ...shopGroupsWithConfig].map(
      ({ name, id }) => ({
        label: name,
        value: id
      })
    );

    setAllRows(mapped);
    setAllShopGroups(
      [domainFakeShopGroup, ...withShops].map(({ name, id }) => ({
        label: name,
        value: id
      }))
    );

    if (!shopGroupId || !withConfig.find(el => el.value === shopGroupId)) {
      setShopGroupId(withConfig[0]?.value);
    }

    setShopGroups(withConfig);
    setMedias(dvMedias);
    setMediaFormats(mappedFormats);
    setLoaded(true);
  };

  const closeModal = () => {
    setModalType('');
    setSelectedConfig(null);
  };

  const onOpenModalUpdate = (_, config) => {
    setSelectedConfig(config);
    setModalType('update');
  };

  const onOpenModalDelete = (_, config) => {
    setSelectedConfig(config);
    setModalType('delete');
  };
  const onOpenModalDuplicate = () => {
    setModalType('duplicate');
  };

  const onCreate = async datas => {
    await CPM_FORMAT.create(datas);
    closeModal();
    setLoaded(false);
  };

  const onUpdate = async datas => {
    await CPM_FORMAT.update(selectedConfig.id, datas);
    closeModal();
    setLoaded(false);
  };

  const onDelete = async () => {
    await CPM_FORMAT.deleteOne(selectedConfig.id);

    closeModal();
    setLoaded(false);
  };

  const onDuplicate = async destinationIds => {
    const sourceId = shopGroupId !== DOMAIN_GROUPID ? shopGroupId : '';
    await CPM_FORMAT.duplicate({ sourceId, destinationIds });

    closeModal();
    setLoaded(false);
  };

  useEffect(() => {
    if (!loaded) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  useEffect(() => {
    if (loaded) {
      const filteredRows = allRows.filter(el => el.shopGroupId === shopGroupId);

      setRows(filteredRows);
    }
    // eslint-disable-next-line
  }, [allRows, shopGroupId, loaded]);

  return (
    <Container spacing={5} style={{ padding: '20px' }}>
      <Loading loading={!loaded}>
        {shopGroups.length !== 0 ? (
          <>
            <Row>
              <Item xs={4}>
                <Select
                  title="Configuration"
                  options={shopGroups}
                  value={shopGroupId}
                  onChange={setShopGroupId}
                />
              </Item>
              <Item xs justify="flex-end">
                <ButtonCreate
                  disabled={!isSuperAdmin}
                  onClick={() => {
                    setModalType('create');
                  }}
                >
                  Ajouter une configuration
                </ButtonCreate>
              </Item>
            </Row>
            <Row>
              <Item>
                <TableData
                  headers={[
                    {
                      label: 'Média',
                      id: 'media',
                      sortKey: 'media'
                    },
                    { label: 'Formats', id: 'listFormats' },
                    {
                      label: 'CPM',
                      id: 'cpm',
                      sortKey: 'cpm',
                      help:
                        'CPM appliqué sur le setup automatique pour le média en question.'
                    },
                    {
                      label: 'CPM mobile',
                      id: 'mobileCpm',
                      sortKey: 'mobileCpm',
                      help:
                        'CPM appliqué sur le setup automatique de campagnes mobiles pour le média en question.'
                    }
                  ]}
                  hidenFields={['_id']}
                  rows={rows}
                  onUpdate={isSuperAdmin && onOpenModalUpdate}
                  onDelete={isSuperAdmin && onOpenModalDelete}
                />
              </Item>
            </Row>
            <Row>
              <Item>
                <ButtonCreate
                  disabled={!isSuperAdmin}
                  IconComponent={ChevronRightIcon}
                  onClick={isSuperAdmin && onOpenModalDuplicate}
                >
                  Dupliquer la configuration
                </ButtonCreate>
              </Item>
            </Row>
          </>
        ) : (
          <>
            <Row>
              <Item>
                <Text>
                  Aucune configuration sur ce domaine. Ajoutez une
                  configuration.
                </Text>
              </Item>
            </Row>
            <Row>
              <Item xs>
                <ButtonCreate
                  disabled={!isSuperAdmin}
                  justify="center"
                  onClick={() => {
                    setModalType('create');
                  }}
                >
                  Ajouter une configuration
                </ButtonCreate>
              </Item>
            </Row>
          </>
        )}
      </Loading>
      {modalType === 'delete' && (
        <ModalDelete
          open
          onClose={closeModal}
          onDelete={onDelete}
          type="cette configuration"
        />
      )}
      {modalType === 'create' && (
        <ModalForm
          config={{
            medias,
            shopGroups: allShopGroups,
            mediaFormats,
            otherConfigs: allRows,
            selected: { shopGroupId }
          }}
          onClose={closeModal}
          onValidate={onCreate}
          domainGroupId={DOMAIN_GROUPID}
        />
      )}

      {modalType === 'update' && (
        <ModalForm
          config={{
            medias,
            shopGroups: allShopGroups,
            mediaFormats,
            selected: selectedConfig,
            otherConfigs: rows
          }}
          domainGroupId={DOMAIN_GROUPID}
          onClose={closeModal}
          onValidate={onUpdate}
        />
      )}

      {modalType === 'duplicate' && (
        <ModalDuplicate
          open
          onClose={closeModal}
          onValidate={onDuplicate}
          otherConfigs={allRows}
          domainGroupId={DOMAIN_GROUPID}
          shopGroups={allShopGroups?.reduce((acc, { label, value }) => {
            if (value !== shopGroupId) {
              acc.push({
                label,
                key: value
              });
            }
            return acc;
          }, [])}
        />
      )}
    </Container>
  );
};

export default CpmFormatContainer;
